import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Building2, IndianRupee, ListTree, PieChart } from "lucide-react";
import { getCompanyList, getPayrollCtc } from "../../Action/courseAction";
import {
  CustomAdminTable,
  CustomBadge,
  CustomButton,
  CustomModal,
  CustomPageLayout,
  CustomStatCard,
} from "../../Component/customComponents";
import {
  buildPeriodFilterFields,
  currentMonth,
  currentYear,
  money,
  percent,
  periodLabel,
} from "../Common/payrollCommon";

const EMPTY_RESULT = { companyDetails: {}, data: [] };

const emptyFilters = (company = "") => ({
  year: currentYear(),
  startMonth: currentMonth(),
  endMonth: currentMonth(),
  company,
});

/** Request body for getPayrollCtc (same keys as before) */
const buildReqBody = (f) => {
  const body = {
    year: f.year?._id,
    startMonth: f.startMonth?._id,
    endMonth: f.endMonth?._id,
    companyId: f.company?._id,
  };
  if (!f.company) delete body.companyId;
  return body;
};

/** department → sub departments  ⇒  one row per sub department */
const flattenCtc = (departments) =>
  (departments || []).flatMap((dept, d) =>
    (dept?.subDepartments || []).map((sub, s) => ({
      key: `${d}-${s}`,
      department: dept?.department,
      departmentShare: dept?.depContributionPercentage,
      subDepartment: sub?.subDepartmentName,
      monthlyCtc: sub?.totalSalary,
      subShare: sub?.subdepContributionPercentage,
    })),
  );

/** 0–100 bar; non-numeric values draw nothing */
const ShareBar = ({ value }) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return (
    <div className="progress mt-1" style={{ height: 6 }} aria-hidden="true">
      <div
        className="progress-bar"
        style={{ width: `${Math.max(0, Math.min(100, n))}%`, background: "var(--cui-primary)" }}
      />
    </div>
  );
};

/** Department-wise contribution for the popup */
const ContributionBreakdown = ({ departments, total }) => (
  <div className="d-flex flex-column gap-3">
    {departments.map((dept, i) => (
      <div key={`${dept?.department}-${i}`} className="border rounded-3 overflow-hidden">
        <div className="px-3 py-2" style={{ background: "var(--cui-primary-soft)" }}>
          <div className="d-flex justify-content-between gap-3 fw-bold" style={{ color: "var(--cui-primary)" }}>
            <span>{dept?.department || "—"}</span>
            <span>{percent(dept?.depContributionPercentage)}</span>
          </div>
          <ShareBar value={dept?.depContributionPercentage} />
        </div>
        {(dept?.subDepartments || []).map((sub, j) => (
          <div key={`${sub?.subDepartmentName}-${j}`} className="px-3 py-2 border-top small">
            <div className="d-flex justify-content-between gap-3">
              <span className="text-muted">{sub?.subDepartmentName || "—"}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {money(sub?.totalSalary)} · {percent(sub?.subdepContributionPercentage)}
              </span>
            </div>
          </div>
        ))}
      </div>
    ))}
    <div className="d-flex justify-content-between gap-3 px-1 fw-bold fs-6">
      <span>Total Salary</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(total)}</span>
    </div>
  </div>
);

const PayRollCtc = () => {
  const dispatch = useDispatch();

  const [filters, setFilters] = useState(emptyFilters);
  const [companyList, setCompanyList] = useState([]);
  const [defaultCompany, setDefaultCompany] = useState("");
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ---------- API (getPayrollCtc always calls back, {} on failure) ---------- */
  const fetchCtc = (f) => {
    setLoading(true);
    dispatch(
      getPayrollCtc(buildReqBody(f), (resp) => {
        setResult(resp?.statusCode === 200 ? { ...EMPTY_RESULT, ...resp } : EMPTY_RESULT);
        setPage(1);
        setLoading(false);
      }),
    );
  };

  useEffect(() => {
    dispatch(
      getCompanyList((res) => {
        // the EDUTECH company only, as before
        const company = (res || []).find((item) => item?.name?.toUpperCase().includes("EDUTECH")) || "";
        setCompanyList(company ? [company] : []);
        setDefaultCompany(company);
        const initial = emptyFilters(company);
        setFilters(initial);
        fetchCtc(initial); // this month on open, as before
      }),
    );
  }, []);

  /* ---------- filter drawer ---------- */
  const filterFields = useMemo(
    () => [
      ...buildPeriodFilterFields(),
      {
        key: "company",
        label: "Company",
        type: "dropdown",
        options: companyList,
        defaultValue: () => defaultCompany,
        disabled: true, // fixed to EDUTECH, as before
        countAsFilter: false,
      },
    ],
    [companyList, defaultCompany],
  );

  /* ---------- table ---------- */
  const departments = result?.data || [];
  const allRows = useMemo(() => flattenCtc(departments), [departments]);
  const totalSalary = result?.companyDetails?.totalSalary;
  const period = periodLabel(filters);

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return allRows;
    return allRows.filter((row) =>
      [row.department, row.subDepartment].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [allRows, searchText]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const columns = [
    {
      key: "department",
      header: "Department",
      minWidth: 150,
      render: (v) =>
        v ? (
          <CustomBadge tone="info" size="sm">
            {v}
          </CustomBadge>
        ) : (
          "—"
        ),
    },
    { key: "subDepartment", header: "Sub Dept", minWidth: 150, render: (v) => <strong>{v || "—"}</strong> },
    {
      key: "monthlyCtc",
      header: "Monthly CTC",
      align: "right",
      minWidth: 140,
      render: (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(v)}</span>,
    },
    {
      key: "subShare",
      header: "Sub Contribution",
      minWidth: 150,
      render: (v) => (
        <div>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{percent(v)}</span>
          <ShareBar value={v} />
        </div>
      ),
    },
    {
      key: "departmentShare",
      header: "Total Contribution",
      minWidth: 150,
      render: (v) => (
        <div>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{percent(v)}</span>
          <ShareBar value={v} />
        </div>
      ),
    },
  ];

  return (
    <CustomPageLayout title="Salary Analysis">
      <div className="d-flex flex-column gap-3">
        {/* ---------- summary ---------- */}
        <div className="row g-3">
          <CustomStatCard
            colClass="col-md-4 col-12"
            title="Total Salary"
            value={money(totalSalary)}
            subtitle={period || "Selected period"}
            icon={<IndianRupee size={18} />}
            tone="primary"
            loading={loading}
            onClick={departments.length ? () => setBreakdownOpen(true) : undefined}
          />
          <CustomStatCard
            colClass="col-md-4 col-sm-6 col-12"
            title="Departments"
            value={departments.length}
            subtitle={defaultCompany?.name || "Company"}
            icon={<Building2 size={18} />}
            tone="info"
            loading={loading}
          />
          <CustomStatCard
            colClass="col-md-4 col-sm-6 col-12"
            title="Sub Departments"
            value={allRows.length}
            subtitle="Across all departments"
            icon={<ListTree size={18} />}
            tone="warning"
            loading={loading}
          />
        </div>

        {/* ---------- sub departments ---------- */}
        <CustomAdminTable
          id="payroll-ctc-table"
          title={
            <span className="d-flex flex-column">
              {`Sub Department CTC (${allRows.length})`}
              {period && (
                <small className="text-muted fw-normal" style={{ fontSize: 12 }}>
                  {period}
                </small>
              )}
            </span>
          }
          columns={columns}
          data={pagedRows}
          loading={loading}
          rowKey={(row) => row.key}
          enableSerialNumber
          serialHeader="S.No"
          search={{
            value: searchText,
            onChange: (v) => {
              setSearchText(v);
              setPage(1);
            },
            placeholder: "Search department or sub department...",
          }}
          toolbarActions={
            <CustomButton
              variant="secondary"
              icon={<PieChart size={15} />}
              onClick={() => setBreakdownOpen(true)}
              disabled={!departments.length}
              title="Department-wise contribution"
            >
              Total: {money(totalSalary)}
            </CustomButton>
          }
          advancedFilters={filterFields}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchCtc(values);
          }}
          applyFiltersOnReset
          filterSubtitle="Pick the year and month range"
          pagination={{
            page,
            pageSize,
            total: filteredRows.length,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
          emptyTitle={searchText ? "No matching departments" : "No salary data"}
          emptyDescription={searchText ? "Try a different search." : "No CTC data for this period."}
          maxHeight="63vh"
        />
      </div>

      <CustomModal
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        title="Department Contribution"
        subtitle={period}
        icon={<PieChart size={18} />}
        size="md"
        footer={
          <CustomButton variant="outline" onClick={() => setBreakdownOpen(false)}>
            Close
          </CustomButton>
        }
      >
        <ContributionBreakdown departments={departments} total={totalSalary} />
      </CustomModal>
    </CustomPageLayout>
  );
};

export default PayRollCtc;
